import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, IconButton,
  InputAdornment, LinearProgress, MenuItem, Paper, Stack, Tab, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import LinkIcon from "@mui/icons-material/Link";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  useGetBanquetServicesMutation, useSaveBanquetServiceMutation,
  useDeleteBanquetServiceMutation, useSeedBanquetServicesMutation,
  useGetBanquetServiceImagesMutation, useSaveBanquetServiceImageMutation,
  useDeleteBanquetServiceImageMutation, useReorderBanquetServiceImagesMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import ImageUploader from "./ImageUploader";
import { PriceInline, PriceCard } from "./PriceDisplay";
import { uploadImage } from "../../../Components/UploadFile/upload";
import EditServiceModal, { EditServiceItem, ServiceImageItem } from "./EditServiceModal";

/* ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", greenBd: "#a8d5ac", redTx: "#9b2c2c",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};

type ServiceType = string;
type PricingType = "pp" | "flat" | "included";

interface MainCategory {
  id: number; name: string; type: string | null; icon: string | null; sort_order: number;
}

interface ServiceItem {
  id: number; name: string; serviceType: ServiceType;
  pricingType?: PricingType; price?: number; base_price?: number | null;
  image?: string | null; category?: string;
  durationHours?: number; tier?: string;
  images?: ServiceImageItem[];
}
interface ServiceForm {
  id?: number; name: string; price: string; base_price: string;
  image: string; pricingType: PricingType;
  category: string; durationHours: string;
}

const ICON_MAP: Record<string, React.ReactElement> = {
  RestaurantMenuOutlined: <RestaurantMenuOutlinedIcon sx={{ fontSize: 17 }} />,
  LocalBarOutlined: <LocalBarOutlinedIcon sx={{ fontSize: 17 }} />,
  RestaurantOutlined: <RestaurantOutlinedIcon sx={{ fontSize: 17 }} />,
  ExtensionOutlined: <ExtensionOutlinedIcon sx={{ fontSize: 17 }} />,
  TvOutlined: <TvOutlinedIcon sx={{ fontSize: 17 }} />,
};
const getTabIcon = (iconName: string | null) => ICON_MAP[iconName ?? ""] ?? <ExtensionOutlinedIcon sx={{ fontSize: 17 }} />;

// API helper for main categories (uses same auth pattern)
const API_BASE = process.env.REACT_APP_API_URL || "";
const mcHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});
const mcApi = {
  get: (p: string) => fetch(`${API_BASE}${p}`, { headers: mcHeaders() }).then(r => r.json()),
  post: (p: string, b: any) => fetch(`${API_BASE}${p}`, { method: "POST", headers: mcHeaders(), body: JSON.stringify(b) }).then(r => r.json()),
  del: (p: string) => fetch(`${API_BASE}${p}`, { method: "DELETE", headers: mcHeaders() }).then(r => r.json()),
};

const PT: { value: PricingType; label: string }[] = [
  { value: "pp", label: "Per Person" }, { value: "flat", label: "Flat Fee" }, { value: "included", label: "Included" },
];
const PKG_CATS = ["Standard", "Premium", "Deluxe", "Other"];
const DEFAULT_MENU_CATS = ["Indian", "Sri Lankan", "Tamil", "Western", "Chinese", "Thai", "Caribbean", "Middle Eastern", "Buffet", "Vegetarian", "Non-Vegetarian", "Premium", "Other"];
const ADDON_CATS = ["Chairs & Tables", "Audio & Visual", "Transportation", "Entertainment", "Flowers", "Event Decor", "Officiant", "Beauty", "The Little Extras", "Additional Services"];
const EMPTY: ServiceForm = { id: undefined, name: "", price: "0", base_price: "", image: "", pricingType: "pp", category: "", durationHours: "5" };

/* ── Images ── */
const IMG: Record<string, string> = {
  "Indian": "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=300&fit=crop",
  "Tamil": "https://images.unsplash.com/photo-1630383249896-424e482df921?w=400&h=300&fit=crop",
  "Sri Lankan": "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=400&h=300&fit=crop",
  "Western": "https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=300&fit=crop",
  "Chinese": "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=400&h=300&fit=crop",
  "Thai": "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=400&h=300&fit=crop",
  "Caribbean": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop",
  "Middle Eastern": "https://images.unsplash.com/photo-1540914124281-342587941389?w=400&h=300&fit=crop",
  "Buffet": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
  "Vegetarian": "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop",
  "Non-Vegetarian": "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=400&h=300&fit=crop",
  "Premium": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop",
  "Standard": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=300&fit=crop",
  "Deluxe": "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop",
  "Chairs & Tables": "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=300&fit=crop",
  "Audio & Visual": "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
  "Entertainment": "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400&h=300&fit=crop",
  "Flowers": "https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=400&h=300&fit=crop",
  "Event Decor": "https://images.unsplash.com/photo-1478146059778-26a4c1fd8f31?w=400&h=300&fit=crop",
  "Other": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
};
const COLORS: Record<string, string> = {
  "Indian": "#e65100", "Tamil": "#e65100", "Sri Lankan": "#bf360c", "Western": "#4a148c",
  "Chinese": "#c62828", "Thai": "#00695c", "Caribbean": "#f57f17", "Middle Eastern": "#5d4037",
  "Buffet": "#1565c0", "Vegetarian": "#2e7d32", "Non-Vegetarian": "#d84315", "Premium": "#8d6e00",
  "Standard": "#1565c0", "Deluxe": "#8d6e00", "Other": "#546e7a",
};
/* Name-based image lookup (for items where category alone isn't enough) */
const NAME_IMG: Record<string, string> = {
  "south indian": "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=300&fit=crop",
  "north indian": "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=400&h=300&fit=crop",
  "sri lankan": "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=400&h=300&fit=crop",
  "tamil": "https://images.unsplash.com/photo-1630383249896-424e482df921?w=400&h=300&fit=crop",
  "afghan": "https://images.unsplash.com/photo-1540914124281-342587941389?w=400&h=300&fit=crop",
  "pakistani": "https://images.unsplash.com/photo-1540914124281-342587941389?w=400&h=300&fit=crop",
  "caribbean": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop",
  "jamaican": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop",
  "mediterranean": "https://images.unsplash.com/photo-1540914124281-342587941389?w=400&h=300&fit=crop",
  "western": "https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=300&fit=crop",
  "thai": "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=400&h=300&fit=crop",
  "chinese": "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=400&h=300&fit=crop",
  "buffet": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
  "vegetarian": "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop",
  "non-veg": "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=400&h=300&fit=crop",
  "premium": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop",
  "plated": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop",
  "sit-down": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop",
  "family style": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
  "catering": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
  "bar": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=300&fit=crop",
  "cash bar": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=300&fit=crop",
  "open bar": "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop",
  "ticket bar": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=300&fit=crop",
  "customer liquor": "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=300&fit=crop",
  "corkage": "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop",
  "hall rental": "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=300&fit=crop",
  "customize": "https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop",
  "international": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop",
};
const DEFAULT_FOOD_IMG = "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop";

/* Gallery images — 3 per category for rich preview */
const GALLERY: Record<string, string[]> = {
  "Indian": [
    "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800&h=600&fit=crop",
  ],
  "Tamil": [
    "https://images.unsplash.com/photo-1630383249896-424e482df921?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800&h=600&fit=crop",
  ],
  "Sri Lankan": [
    "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&h=600&fit=crop",
  ],
  "Western": [
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=800&h=600&fit=crop",
  ],
  "Chinese": [
    "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1526318896980-cf78c088247c?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=800&h=600&fit=crop",
  ],
  "Thai": [
    "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1559314809-0d155014e29e?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1569562211093-4ed0d0758f12?w=800&h=600&fit=crop",
  ],
  "Caribbean": [
    "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=800&h=600&fit=crop",
  ],
  "Middle Eastern": [
    "https://images.unsplash.com/photo-1540914124281-342587941389?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1529006557810-274b9b2fc783?w=800&h=600&fit=crop",
  ],
  "Buffet": [
    "https://images.unsplash.com/photo-1555244162-803834f70033?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop",
  ],
  "Vegetarian": [
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=800&h=600&fit=crop",
  ],
  "Non-Vegetarian": [
    "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&h=600&fit=crop",
  ],
  "Premium": [
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=800&h=600&fit=crop",
  ],
  "Standard": [
    "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1514362545857-3bc16c8c7f1b?w=800&h=600&fit=crop",
  ],
  "Deluxe": [
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1514362545857-3bc16c8c7f1b?w=800&h=600&fit=crop",
  ],
};
const getGallery = (cat?: string, name?: string): string[] => {
  if (cat && GALLERY[cat]) return GALLERY[cat];
  if (name) {
    const lower = name.toLowerCase();
    for (const [key, urls] of Object.entries(GALLERY)) {
      if (lower.includes(key.toLowerCase())) return urls;
    }
  }
  return [DEFAULT_FOOD_IMG];
};

/** Get image for an item — tries: category → name keywords → default */
const getImg = (cat?: string, name?: string): string => {
  if (cat && IMG[cat]) return IMG[cat];
  if (name) {
    const lower = name.toLowerCase();
    for (const [key, url] of Object.entries(NAME_IMG)) {
      if (lower.includes(key)) return url;
    }
  }
  return DEFAULT_FOOD_IMG;
};
const getThumb = (cat?: string, name?: string) => getImg(cat, name).replace("w=400&h=300", "w=80&h=80");
const getColor = (c?: string) => COLORS[c || ""] || "#546e7a";
const PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23f0ebe6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca8b7' font-size='10'%3ENo Img%3C/text%3E%3C/svg%3E";
const fmtM = (v?: number | string) => { const n = Number(v ?? 0); return n ? "$" + n.toLocaleString("en-CA") : "—"; };

const inputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg,
    border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold },
    transition: "all 0.15s", "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

/* ═══════════════════════════════════════════════════════════════════════════
   SMART NAME FIELD (stable — outside main component)
   ═══════════════════════════════════════════════════════════════════════════ */
const SmartNameField: React.FC<{
  value: string; onChange: (v: string) => void; onSelect: (i: ServiceItem) => void;
  items: ServiceItem[]; placeholder?: string;
}> = ({ value, onChange, onSelect, items, placeholder }) => {
  const [local, setLocal] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => { setLocal(value); }, [value]);

  const filtered = useMemo(() => {
    const q = local.toLowerCase();
    return q ? items.filter(i => i.name.toLowerCase().includes(q)) : items.slice(0, 20);
  }, [local, items]);

  React.useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <TextField size="small" fullWidth value={local}
        onChange={e => { setLocal(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => onChange(local)}
        placeholder={placeholder || "Search..."} autoComplete="off"
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
        sx={inputSx}
      />
      {open && filtered.length > 0 && (
        <Paper elevation={12} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1400, mt: 0.5, maxHeight: 320, overflowY: "auto", borderRadius: "12px", border: `1px solid ${T.line}` }}>
          {filtered.map(item => {
            const cat = item.category ?? item.tier ?? "";
            return (
              <Box key={item.id}
                onMouseDown={e => { e.preventDefault(); setLocal(item.name); onSelect(item); setOpen(false); }}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.2, cursor: "pointer", borderBottom: `1px solid ${T.line}`, "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: T.peach }, transition: "background 0.1s" }}>
                <Box component="img" src={getThumb(cat, item.name)} onError={(e: any) => { e.target.src = PH; }}
                  sx={{ width: 36, height: 36, borderRadius: "8px", objectFit: "cover", flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>{item.name}</Typography>
                  {cat && <Typography sx={{ fontSize: 10, color: getColor(cat), mt: 0.2 }}>{cat}</Typography>}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy, flexShrink: 0 }}>{fmtM(item.price)}</Typography>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   LED SCREENS TAB (inline component)
   ═══════════════════════════════════════════════════════════════════════════ */
const LED_HALLS = [
  { key: "Nova", capacity: 250, color: "#1565c0", bg: "#e8f0fe" },
  { key: "Titan", capacity: 280, color: "#2e7d32", bg: "#e6f4ea" },
  { key: "Lunar", capacity: 260, color: "#e65100", bg: "#fff3e0" },
  { key: "Eclipse", capacity: 250, color: "#6a1b9a", bg: "#f3e5f5" },
];
const LED_ITEMS = [
  { id: 1, name: "LED 1", desc: "Main stage left", price: 3000 },
  { id: 2, name: "LED 2", desc: "Main stage right", price: 3000 },
  { id: 3, name: "LED 3", desc: "Side panel", price: 1800 },
  { id: 4, name: "LED 4", desc: "Rear projection", price: 1800 },
];

function getLedStatus(led: string, halls: string[]): { state: "included" | "available" | "disabled"; reason: string } {
  if (halls.length === 0) return { state: "disabled", reason: "Select a hall first" };
  const hasNova = halls.includes("Nova"), hasEcl = halls.includes("Eclipse"), hasTL = halls.includes("Titan") || halls.includes("Lunar");
  if (led === "LED 1") return hasNova ? { state: "included", reason: "Included with Nova" } : hasTL ? { state: "disabled", reason: "Not available for Titan/Lunar" } : { state: "disabled", reason: "Only with Nova" };
  if (led === "LED 2") return hasEcl ? { state: "included", reason: "Included with Eclipse" } : hasTL ? { state: "disabled", reason: "Not available for Titan/Lunar" } : { state: "disabled", reason: "Only with Eclipse" };
  return { state: "available", reason: "Available for all halls" };
}

/* Bundle definitions */
const LED_BUNDLES = [
  { id: "mid-combo", name: "Middle Hall LED Bundle", halls: ["Titan", "Lunar"], leds: ["LED 3", "LED 4"], type: "flat" as const, discount: 300, desc: "Save $300 when using LED 3 + LED 4 together" },
  { id: "full-pkg", name: "Full Venue LED Package", halls: ["Nova", "Titan", "Lunar", "Eclipse"], leds: ["LED 3", "LED 4"], type: "flat" as const, discount: 500, desc: "Save $500 with all halls + full LED coverage" },
  { id: "nova-titan", name: "Nova + Titan Combo", halls: ["Nova", "Titan"], leds: ["LED 3"], type: "percent" as const, discount: 10, desc: "10% off LED 3 with Nova + Titan" },
];

/* Recommendation engine */
function getRecommendedLEDs(halls: string[]): { leds: string[]; reason: string } {
  if (halls.length === 0) return { leds: [], reason: "" };
  const rec: string[] = [];
  if (halls.includes("Nova")) rec.push("LED 1");
  if (halls.includes("Eclipse")) rec.push("LED 2");
  const hasTitan = halls.includes("Titan"), hasLunar = halls.includes("Lunar");
  if (hasTitan && hasLunar) { rec.push("LED 3", "LED 4"); }
  else if (hasTitan) rec.push("LED 3");
  else if (hasLunar) rec.push("LED 4");
  const reason = halls.length === 4 ? "Full venue coverage — all 4 screens recommended" : `Optimal setup for ${halls.join(" + ")}`;
  return { leds: Array.from(new Set(rec)), reason };
}

const HALL_ORDER = ["Nova", "Titan", "Lunar", "Eclipse"];

const LEDScreensTab: React.FC = () => {
  /* ── State ── */
  const [selectedHalls, setSelectedHalls] = useState<string[]>([]);
  const [selectedLEDs, setSelectedLEDs] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [adjacencyWarning, setAdjacencyWarning] = useState("");

  /* ── Contiguous selection helpers ── */
  const isContiguous = (halls: string[]): boolean => {
    if (halls.length <= 1) return true;
    const idxs = halls.map(h => HALL_ORDER.indexOf(h)).filter(i => i >= 0).sort((a, b) => a - b);
    for (let i = 1; i < idxs.length; i++) { if (idxs[i] !== idxs[i - 1] + 1) return false; }
    return true;
  };

  const getAllowedHalls = (): string[] => {
    if (selectedHalls.length === 0) return HALL_ORDER;
    const idxs = selectedHalls.map(h => HALL_ORDER.indexOf(h)).filter(i => i >= 0);
    const min = Math.min(...idxs);
    const max = Math.max(...idxs);
    return HALL_ORDER.filter((_, i) => (i >= min && i <= max) || i === min - 1 || i === max + 1);
  };

  const allowedHalls = getAllowedHalls();

  /* ── Hall toggle with adjacency check ── */
  const toggleHall = (hall: string) => {
    const next = selectedHalls.includes(hall) ? selectedHalls.filter(h => h !== hall) : [...selectedHalls, hall];

    if (!isContiguous(next)) {
      setAdjacencyWarning("Only adjacent halls can be selected");
      setTimeout(() => setAdjacencyWarning(""), 2500);
      return;
    }

    setSelectedHalls(next);
    // Auto-remove LEDs that become disabled
    setSelectedLEDs(sl => sl.filter(led => getLedStatus(led, next).state !== "disabled"));
    setAdjacencyWarning("");
  };

  /* ── LED status per screen ── */
  const ledStatuses = LED_ITEMS.map(led => ({ ...led, status: getLedStatus(led.name, selectedHalls) }));
  const includedLEDs = ledStatuses.filter(l => l.status.state === "included").map(l => l.name);

  // Auto-include LEDs that are "included" state
  const effectiveLEDs = Array.from(new Set([...selectedLEDs, ...includedLEDs]));

  /* ── LED toggle ── */
  const toggleLED = (name: string) => {
    const st = getLedStatus(name, selectedHalls);
    if (st.state === "disabled" || st.state === "included") return;
    setSelectedLEDs(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };
  const removeLED = (name: string) => {
    if (includedLEDs.includes(name)) return; // can't remove included
    setSelectedLEDs(prev => prev.filter(x => x !== name));
  };

  /* ── Pricing calculations ── */
  const ledPricing: Record<string, number> = {};
  LED_ITEMS.forEach(l => { ledPricing[l.name] = l.price; });

  const rawLedTotal = effectiveLEDs.reduce((sum, led) => sum + (ledPricing[led] || 0), 0);
  const finalLedTotal = effectiveLEDs.reduce((sum, led) => {
    if (includedLEDs.includes(led)) return sum;
    return sum + (ledPricing[led] || 0);
  }, 0);

  /* ── Bundle discount ── */
  let bundleDiscount = 0;
  const activeBundles: string[] = [];
  if (effectiveLEDs.includes("LED 3") && effectiveLEDs.includes("LED 4")) {
    bundleDiscount += 300;
    activeBundles.push("LED 3 + LED 4 bundle: -$300");
  }
  if (selectedHalls.length === 4) {
    bundleDiscount += 500;
    activeBundles.push("Full venue package: -$500");
  }

  /* ── Grand total ── */
  const grandTotal = Math.max(0, finalLedTotal - bundleDiscount);

  /* ── Recommendation ── */
  const recommendation = getRecommendedLEDs(selectedHalls);

  /* ── Copy offer text ── */
  const offerText = effectiveLEDs.length > 0
    ? `LED Configuration\nHalls: ${selectedHalls.join(", ") || "None"}\n${effectiveLEDs.map(l => `• ${l} — ${includedLEDs.includes(l) ? "Included" : `$${(ledPricing[l] || 0).toLocaleString()}`}`).join("\n")}${bundleDiscount > 0 ? `\nBundle Savings: -$${bundleDiscount.toLocaleString()}` : ""}\nGrand Total: $${grandTotal.toLocaleString()}`
    : "";
  const copyOffer = () => { navigator.clipboard.writeText(offerText); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

      {/* ══════ LEFT COLUMN ══════ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: T.slateLt, mb: 2 }}>
          Select halls to simulate a booking, then configure LED screens. Pricing and availability update automatically.
        </Typography>

        {/* ── PART 1: Hall Selection ── */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", mb: 3, border: `1px solid ${T.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>Select Halls (for preview)</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>Choose halls to see which LEDs apply</Typography>
            </Box>
            {selectedHalls.length === 4 && (
              <Chip label="All halls selected" size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx, border: `1px solid ${T.greenBd}` }} />
            )}
            {selectedHalls.length > 0 && selectedHalls.length < 4 && (
              <Chip label={`${selectedHalls.length} of 4 halls`} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 500, bgcolor: T.peach, color: T.navy }} />
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {LED_HALLS.map(hall => {
              const isSel = selectedHalls.includes(hall.key);
              const isAllowed = allowedHalls.includes(hall.key);
              const isBlocked = !isSel && !isAllowed;
              return (
                <Tooltip key={hall.key} title={isBlocked ? "Only adjacent halls can be selected" : ""} arrow>
                  <Paper elevation={0} onClick={() => !isBlocked && toggleHall(hall.key)}
                    sx={{
                      p: 2, borderRadius: "12px", textAlign: "center",
                      cursor: isBlocked ? "not-allowed" : "pointer",
                      transition: "all 0.18s",
                      opacity: isBlocked ? 0.35 : 1,
                      border: `2px solid ${isSel ? hall.color : "transparent"}`,
                      bgcolor: isSel ? hall.bg : T.bg,
                      boxShadow: isSel ? `0 2px 8px ${hall.color}18` : "none",
                      "&:hover": isBlocked ? {} : { borderColor: hall.color, transform: "translateY(-1px)", boxShadow: `0 4px 12px ${hall.color}15` },
                    }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: isSel ? hall.color : isBlocked ? T.slateLt : T.navy, mb: 0.3 }}>{hall.key}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.slateLt }}>{hall.capacity} guests</Typography>
                    {isSel && <CheckCircleIcon sx={{ fontSize: 16, color: hall.color, mt: 0.5 }} />}
                  </Paper>
                </Tooltip>
              );
            })}
          </Box>
          {adjacencyWarning && (
            <Typography sx={{ fontSize: 11, color: T.redTx, mt: 1, fontWeight: 500, transition: "opacity 0.2s" }}>
              {adjacencyWarning}
            </Typography>
          )}
        </Paper>

        {/* ── No halls message ── */}
        {selectedHalls.length === 0 && (
          <Paper elevation={0} sx={{ p: 4, borderRadius: "14px", textAlign: "center", bgcolor: T.bg, mb: 3, border: `1px dashed ${T.line}` }}>
            <TvOutlinedIcon sx={{ fontSize: 40, color: T.line, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.slateLt }}>No halls selected</Typography>
            <Typography sx={{ fontSize: 12, color: T.slateLt }}>Select halls above to configure LEDs</Typography>
          </Paper>
        )}

        {/* ── PART 3: LED Cards (only show when halls selected) ── */}
        {selectedHalls.length > 0 && (
          <>
            {/* Recommendation banner */}
            {recommendation.leds.length > 0 && (
              <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: "10px", mb: 2, bgcolor: "#fff8e1", border: "1px solid #ffe082" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#f57f17" }}>
                  Recommended: {recommendation.leds.join(", ")}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#f9a825" }}>{recommendation.reason}</Typography>
              </Paper>
            )}

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, mb: 1.5 }}>LED Screens</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
              {ledStatuses.map(led => {
                const { state, reason } = led.status;
                const isIncl = state === "included";
                const isDisabled = state === "disabled";
                const isSel = effectiveLEDs.includes(led.name);
                const stateColor = isIncl ? T.greenTx : isDisabled ? T.slateLt : T.navy;

                return (
                  <Paper key={led.id} elevation={0}
                    onClick={() => !isDisabled && toggleLED(led.name)}
                    sx={{
                      p: 2, borderRadius: "14px", transition: "all 0.15s",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                      border: `2px solid ${isIncl ? T.greenBd : isSel ? T.gold : "transparent"}`,
                      bgcolor: isIncl ? T.greenBg : isSel ? T.peach : T.white,
                      boxShadow: isSel ? `0 2px 12px ${T.gold}20` : "0 1px 2px rgba(0,0,0,0.02)",
                      "&:hover": isDisabled ? {} : { borderColor: isIncl ? T.greenBd : T.gold, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {/* Checkbox / Status indicator */}
                      <Box sx={{
                        width: 22, height: 22, borderRadius: "6px", flexShrink: 0,
                        border: `2px solid ${isIncl ? T.greenTx : isSel ? T.gold : isDisabled ? T.line : T.slateLt}`,
                        bgcolor: isIncl ? T.greenTx : isSel ? T.gold : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {(isIncl || isSel) && <Typography sx={{ fontSize: 12, color: T.white, lineHeight: 1 }}>&#10003;</Typography>}
                      </Box>
                      {/* Icon */}
                      <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: isIncl ? `${T.greenTx}10` : isDisabled ? "#f5f5f5" : T.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <TvOutlinedIcon sx={{ fontSize: 18, color: isIncl ? T.greenTx : isDisabled ? T.line : T.slateLt }} />
                      </Box>
                      {/* Info */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: stateColor }}>{led.name}</Typography>
                          {isIncl && <Chip label="Included" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx, border: `1px solid ${T.greenBd}` }} />}
                          {isDisabled && <Chip label="Not Available" size="small" sx={{ height: 18, fontSize: 8, bgcolor: "#f5f5f5", color: T.slateLt }} />}
                          {!isIncl && !isDisabled && isSel && <Chip label="Selected" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 600, bgcolor: T.peach, color: T.navy }} />}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: T.slateLt }}>{led.desc} &middot; {reason}</Typography>
                      </Box>
                      {/* Price */}
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        {isIncl ? (
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.greenTx }}>$0</Typography>
                        ) : (
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color: isDisabled ? T.slateLt : T.navy }}>${led.price.toLocaleString()}</Typography>
                        )}
                        {isIncl && <Typography sx={{ fontSize: 9, color: T.greenTx, textDecoration: "line-through" }}>${led.price.toLocaleString()}</Typography>}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}

        {/* ── Rules Section ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", bgcolor: T.bg }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>LED Rules</Typography>
          {[
            { t: "Nova → LED 1 included free", c: T.greenTx },
            { t: "Eclipse → LED 2 included free", c: T.greenTx },
            { t: "Titan/Lunar → LED 3 & 4 available (paid)", c: T.navy },
            { t: "LED 1 & 2 blocked for Titan/Lunar only bookings", c: T.redTx },
            { t: "LED 3 + LED 4 together → save $300", c: T.greenTx },
            { t: "All 4 halls booked → save additional $500", c: T.greenTx },
          ].map((r, i) => (
            <Typography key={i} sx={{ fontSize: 11, color: r.c, py: "2px" }}>• {r.t}</Typography>
          ))}
        </Paper>
      </Box>

      {/* ══════ RIGHT COLUMN: Estimate Panel ══════ */}
      <Box sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0, position: { md: "sticky" }, top: { md: 20 } }}>
        <Paper elevation={0} sx={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.03)" }}>
          {/* Header */}
          <Box sx={{ px: 2.5, py: 2, bgcolor: T.navy }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>Estimate Panel</Typography>
            <Typography sx={{ fontSize: 11, color: T.slateLt }}>Live pricing preview</Typography>
          </Box>

          <Box sx={{ p: 2.5 }}>
            {/* Hall count */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: T.slateLt }}>Halls selected</Typography>
              <Chip label={selectedHalls.length > 0 ? selectedHalls.join(", ") : "None"} size="small"
                sx={{ height: 22, fontSize: 10, bgcolor: selectedHalls.length > 0 ? T.peach : T.bg, color: selectedHalls.length > 0 ? T.navy : T.slateLt }} />
            </Box>

            <Divider sx={{ my: 1.5, borderColor: T.line }} />

            {/* LED breakdown */}
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>LED Breakdown</Typography>

            {effectiveLEDs.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: T.slateLt, py: 1, textAlign: "center" }}>No LEDs selected</Typography>
            ) : (
              <Box sx={{ mb: 1.5 }}>
                {effectiveLEDs.map(ledName => {
                  const led = LED_ITEMS.find(l => l.name === ledName);
                  const isIncl = includedLEDs.includes(ledName);
                  if (!led) return null;
                  return (
                    <Box key={ledName} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: "5px", borderBottom: `1px solid ${T.line}`, "&:last-child": { borderBottom: "none" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: T.navy, fontWeight: 500 }}>{led.name}</Typography>
                        {isIncl && <Chip label="FREE" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: T.greenBg, color: T.greenTx }} />}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: isIncl ? T.greenTx : T.navy }}>
                          {isIncl ? "Included" : `$${led.price.toLocaleString()}`}
                        </Typography>
                        {!isIncl && (
                          <IconButton size="small" onClick={() => removeLED(ledName)}
                            sx={{ width: 18, height: 18, "&:hover": { bgcolor: "#fee2e2" } }}>
                            <CloseIcon sx={{ fontSize: 12, color: T.redTx }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Subtotal */}
            {effectiveLEDs.length > 0 && (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: "5px" }}>
                  <Typography sx={{ fontSize: 12, color: T.slateLt }}>LED subtotal (all)</Typography>
                  <Typography sx={{ fontSize: 12, color: T.slateLt }}>${rawLedTotal.toLocaleString()}</Typography>
                </Box>
                {includedLEDs.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: "5px" }}>
                    <Typography sx={{ fontSize: 12, color: T.greenTx }}>Included savings</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.greenTx }}>
                      -${(rawLedTotal - finalLedTotal).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", py: "5px" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>Paid LED total</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>${finalLedTotal.toLocaleString()}</Typography>
                </Box>
              </>
            )}

            {/* Bundle discounts */}
            {bundleDiscount > 0 && (
              <>
                <Divider sx={{ my: 1.5, borderColor: T.line }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.greenTx, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Bundle Savings</Typography>
                {activeBundles.map((b, i) => (
                  <Typography key={i} sx={{ fontSize: 11, color: T.greenTx, py: "2px" }}>&#10003; {b}</Typography>
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.greenTx }}>Total savings</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.greenTx }}>-${bundleDiscount.toLocaleString()}</Typography>
                </Box>
              </>
            )}

            {/* ── GRAND TOTAL ── */}
            <Divider sx={{ my: 1.5, borderColor: T.navy }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: T.navy }}>Grand Total</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy }}>${grandTotal.toLocaleString()}</Typography>
            </Box>
            {bundleDiscount > 0 && (
              <Typography sx={{ fontSize: 10, color: T.greenTx, textAlign: "right" }}>
                You save ${(rawLedTotal - finalLedTotal + bundleDiscount).toLocaleString()} total
              </Typography>
            )}

            {/* Copy button */}
            <Button size="small" variant="contained" fullWidth onClick={copyOffer} disabled={effectiveLEDs.length === 0}
              sx={{ mt: 2, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: "#1b3148" } }}>
              {copied ? "Copied!" : "Copy Estimate"}
            </Button>
          </Box>
        </Paper>

        {/* ── Bundles reference ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", bgcolor: T.bg, mt: 2 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>Available Bundles</Typography>
          {LED_BUNDLES.map(b => {
            const isActive = b.type === "flat"
              ? b.leds.every(l => effectiveLEDs.includes(l)) && b.halls.every(h => selectedHalls.includes(h))
              : false;
            return (
              <Box key={b.id} sx={{ display: "flex", justifyContent: "space-between", py: "5px", borderBottom: `1px solid ${T.line}`, "&:last-child": { borderBottom: "none" }, opacity: isActive ? 1 : 0.6 }}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>{b.name}</Typography>
                    {isActive && <CheckCircleIcon sx={{ fontSize: 12, color: T.greenTx }} />}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: T.slateLt }}>{b.desc}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: isActive ? T.greenTx : T.slateLt, flexShrink: 0 }}>
                  {b.type === "flat" ? `-$${b.discount}` : `${b.discount}%`}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      </Box>
    </Box>
  );
};

const BanquetServices = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const { showMessage, showErrorMessage } = useNotifier();

  // Dynamic main categories
  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [mcLoading, setMcLoading] = useState(true);
  const [mcModalOpen, setMcModalOpen] = useState(false);
  const [mcEdit, setMcEdit] = useState<MainCategory | null>(null);
  const [mcSaving, setMcSaving] = useState(false);
  const [mcDeleteTarget, setMcDeleteTarget] = useState<MainCategory | null>(null);
  const [mcDeleting, setMcDeleting] = useState(false);

  const fetchMainCategories = useCallback(async () => {
    setMcLoading(true);
    try {
      const res = await mcApi.get("/inhouse/main-categories");
      if (res.code === 200 || res.status) setMainCategories(res.data ?? []);
    } catch {}
    setMcLoading(false);
  }, []);

  useEffect(() => { fetchMainCategories(); }, [fetchMainCategories]);

  const [activeTab, setActiveTab] = useState(0);
  const curTab = mainCategories[activeTab];
  const curType = curTab?.type ?? "";
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [form, setForm] = useState<ServiceForm>({ ...EMPTY });
  const [catFilter, setCatFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<ServiceItem | null>(null);
  const [previewItem, setPreviewItem] = useState<ServiceItem | null>(null);
  const [editModalItem, setEditModalItem] = useState<ServiceItem | null>(null);

  // Dynamic categories
  const [customCats, setCustomCats] = useState<string[]>([]);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryIdx, setGalleryIdx] = useState(-1);

  // Media management for current form item
  type MediaItem = { type: "image" | "video"; url: string };
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [mediaUrl, setMediaUrl] = useState("");
  const formRef = React.useRef<HTMLDivElement>(null);

  const [getServices] = useGetBanquetServicesMutation();
  const [saveService, { isLoading: isSaving }] = useSaveBanquetServiceMutation();
  const [deleteService, { isLoading: isDeleting }] = useDeleteBanquetServiceMutation();
  const [seedServices, { isLoading: isSeeding }] = useSeedBanquetServicesMutation();

  // Service images management
  const [getServiceImages] = useGetBanquetServiceImagesMutation();
  const [saveServiceImage] = useSaveBanquetServiceImageMutation();
  const [deleteServiceImage] = useDeleteBanquetServiceImageMutation();
  const [reorderServiceImages] = useReorderBanquetServiceImagesMutation();
  const [serviceImages, setServiceImages] = useState<ServiceImageItem[]>([]);
  const [svcImgUploading, setSvcImgUploading] = useState(false);
  const svcImgInputRef = React.useRef<HTMLInputElement>(null);

  const fetchServiceImages = useCallback(async (serviceId: number) => {
    try {
      const r = (await getServiceImages({ service_id: serviceId }).unwrap()) as any;
      if (r?.status && Array.isArray(r.data)) {
        const imgs = r.data as ServiceImageItem[];
        setServiceImages(imgs);
        // Sync cover image to form for Live Preview
        const cover = imgs.find(i => i.is_cover);
        if (cover) {
          setForm(p => ({ ...p, image: cover.image_url }));
        } else if (imgs.length > 0 && !form.image) {
          // No cover set — use first image as preview
          setForm(p => ({ ...p, image: imgs[0].image_url }));
        }
      }
      else setServiceImages([]);
    } catch { setServiceImages([]); }
  }, [getServiceImages, form.image]);

  const handleSvcImgUpload = async (file: File) => {
    if (!form.id) return;
    setSvcImgUploading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || "";
      const path = await uploadImage(file);
      if (path) {
        const fullUrl = `${API_URL}/../${path}`;
        // Auto-set as cover if this is the first image
        const isFirst = serviceImages.length === 0;
        const r = (await saveServiceImage({ service_id: form.id, image_url: fullUrl, sort_order: serviceImages.length, is_cover: isFirst || undefined }).unwrap()) as any;
        if (r?.status) {
          showMessage("Image added");
          // If first image or no cover yet, sync to form for instant Live Preview
          if (isFirst || !form.image) {
            setForm(p => ({ ...p, image: fullUrl }));
          }
          fetchServiceImages(form.id);
          fetchItems(curType);
        }
        else showErrorMessage("Failed to save image");
      }
    } catch { showErrorMessage("Upload failed"); }
    finally { setSvcImgUploading(false); }
  };

  const handleDeleteSvcImg = async (imgId: number) => {
    try {
      const deleted = serviceImages.find(i => i.id === imgId);
      const r = (await deleteServiceImage(imgId).unwrap()) as any;
      if (r?.status) {
        showMessage("Image removed");
        // If deleted image was the cover, clear form.image so preview updates
        if (deleted?.is_cover || deleted?.image_url === form.image) {
          const remaining = serviceImages.filter(i => i.id !== imgId);
          const newCover = remaining.find(i => i.is_cover) || remaining[0];
          setForm(p => ({ ...p, image: newCover?.image_url || "" }));
        }
        if (form.id) { fetchServiceImages(form.id); fetchItems(curType); }
      }
    } catch { showErrorMessage("Failed"); }
  };

  const handleSetCover = async (img: ServiceImageItem) => {
    try {
      const r = (await saveServiceImage({ id: img.id, service_id: img.service_id, image_url: img.image_url, is_cover: true, sort_order: img.sort_order }).unwrap()) as any;
      if (r?.status) {
        showMessage("Cover set");
        // Immediately sync cover to form for Live Preview
        setForm(p => ({ ...p, image: img.image_url }));
        if (form.id) fetchServiceImages(form.id);
      }
    } catch { showErrorMessage("Failed"); }
  };

  const handleSvcImgReorder = async (fromIdx: number, toIdx: number) => {
    const reordered = [...serviceImages];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setServiceImages(reordered);
    try {
      await reorderServiceImages({ items: reordered.map(i => i.id) }).unwrap();
    } catch {}
  };

  const handleUpdateSvcImgTitle = async (img: ServiceImageItem, title: string) => {
    try {
      await saveServiceImage({ id: img.id, service_id: img.service_id, image_url: img.image_url, title, tag: img.tag, is_cover: img.is_cover, sort_order: img.sort_order }).unwrap();
      if (form.id) fetchServiceImages(form.id);
    } catch {}
  };

  const handleUpdateSvcImgTag = async (img: ServiceImageItem, tag: string) => {
    try {
      await saveServiceImage({ id: img.id, service_id: img.service_id, image_url: img.image_url, title: img.title, tag: tag || null, is_cover: img.is_cover, sort_order: img.sort_order }).unwrap();
      if (form.id) fetchServiceImages(form.id);
    } catch {}
  };

  const openGallery = (images: string[], startIdx: number = 0) => { setGalleryImages(images); setGalleryIdx(startIdx); };
  const closeGallery = () => { setGalleryImages([]); setGalleryIdx(-1); };

  const fetchItems = useCallback(async (st: ServiceType) => {
    setIsFetching(true);
    try { const r = (await getServices({ serviceType: st } as any).unwrap()) as any; setItems(r?.status && Array.isArray(r.data) ? r.data : []); }
    catch { setItems([]); } finally { setIsFetching(false); }
  }, [getServices]);

  useEffect(() => { setForm({ ...EMPTY }); setCatFilter("all"); setPreviewItem(null); if (curType && curType !== "led") fetchItems(curType as any); }, [activeTab, curType]);

  const setField = (f: keyof ServiceForm, v: string) => setForm(p => ({ ...p, [f]: v }));
  const handleClear = () => { setForm({ ...EMPTY }); setPreviewItem(null); setMedia([]); setServiceImages([]); };
  const handleEdit = (item: ServiceItem) => {
    setForm({ id: item.id, name: item.name, price: String(item.price ?? 0), base_price: String(item.base_price ?? ""), image: item.image ?? "", pricingType: item.pricingType ?? "pp", category: item.category ?? item.tier ?? "", durationHours: String(item.durationHours ?? 5) });
    setPreviewItem(item);
    // Load default gallery images for this category
    const cat = item.category ?? item.tier ?? "";
    setMedia(getGallery(cat, item.name).map(url => ({ type: "image" as const, url })));
    // Fetch service images (either from eager-loaded data or API)
    if (item.images && item.images.length > 0) setServiceImages(item.images);
    else fetchServiceImages(item.id);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showErrorMessage("Name is required"); return; }
    try {
      const r = (await saveService({ id: form.id, name: form.name.trim(), category: form.category || undefined, price: Number(form.price) || 0, base_price: form.base_price ? Number(form.base_price) : undefined, image: form.image || undefined, serviceType: curType, pricingType: form.pricingType, durationHours: Number(form.durationHours) || 0 } as any).unwrap()) as any;
      if (r?.status) { showMessage(r.message ?? "Saved"); handleClear(); fetchItems(curType); } else showErrorMessage(r?.message ?? "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { const r = (await deleteService(deleteTarget.id).unwrap()) as any; if (r?.status) { showMessage("Deleted"); fetchItems(curType); } else showErrorMessage("Failed"); }
    catch { showErrorMessage("Failed"); } finally { setDeleteTarget(null); }
  };

  const handleSeed = async () => {
    try { const r = (await seedServices().unwrap()) as any; showMessage(r?.message ?? "Seeded"); fetchItems(curType); }
    catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed"); }
  };

  const handleModalSave = async (data: {
    id: number; name: string; price: number; base_price?: number;
    image?: string; category?: string; pricingType: string; durationHours?: number;
  }) => {
    try {
      const r = (await saveService({ ...data, serviceType: curType } as any).unwrap()) as any;
      if (r?.status) {
        showMessage("Updated successfully");
        setEditModalItem(null);
        fetchItems(curType);
        // Update preview panel if same item
        if (previewItem?.id === data.id) {
          setPreviewItem({ ...previewItem, ...data, base_price: data.base_price ?? null } as any);
        }
      } else showErrorMessage(r?.message ?? "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message ?? "Failed"); }
  };

  const norm = (s?: string) => (s || "").toLowerCase().replace(/[\s\-_]+/g, "");
  const filtered = catFilter === "all" ? items : items.filter(i => norm(i.category ?? i.tier) === norm(catFilter));
  // Build dynamic category list: defaults + custom + any found in data
  const menuCatsDynamic = useMemo(() => {
    const base = new Set([...DEFAULT_MENU_CATS, ...customCats]);
    items.forEach(i => { if (i.category) base.add(i.category); if (i.tier) base.add(i.tier); });
    return Array.from(base);
  }, [items, customCats]);

  const catOpts = curType === "package" ? PKG_CATS : curType === "addon" ? ADDON_CATS : curType === "menu" ? menuCatsDynamic : [];
  const isEditing = Boolean(form.id);

  /* ── Selling price differs from base? ── */
  const priceDiffers = form.base_price && Number(form.base_price) > 0 && Number(form.price) !== Number(form.base_price);

  /* ── Form fields ── */
  const formFields = (() => {
    const selectItem = (item: ServiceItem) => { setForm(p => ({ ...p, name: item.name, price: String(item.price ?? 0), base_price: String(item.base_price ?? ""), image: item.image ?? "", category: item.category ?? item.tier ?? "", durationHours: String(item.durationHours ?? 5) })); setPreviewItem(item); };
    const nameField = <SmartNameField value={form.name} onChange={v => setField("name", v)} onSelect={selectItem} items={items} placeholder={`Search ${curType}...`} />;
    const priceField = (
      <TextField size="small" fullWidth type="number" label="Selling Price ($)" value={form.price}
        onChange={e => setField("price", e.target.value)} inputProps={{ min: 0, step: 0.01 }}
        sx={{
          ...inputSx,
          ...(priceDiffers ? {
            "& .MuiInputBase-root": {
              ...inputSx["& .MuiInputBase-root"],
              border: `1.5px solid #e67e22`,
              bgcolor: "#fef6ee",
            },
          } : {}),
        }}
      />
    );
    const basePriceField = (
      <TextField size="small" fullWidth type="number" label="Base Price ($)" value={form.base_price}
        onChange={e => setField("base_price", e.target.value)} inputProps={{ min: 0, step: 0.01 }}
        sx={inputSx}
      />
    );

    if (curType === "food" || curType === "bar") return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr" }, gap: 2.5 }}>
        <Box>{nameField}</Box><Box>{basePriceField}</Box><Box>{priceField}</Box>
        <Box><TextField select size="small" fullWidth label="Pricing Type" value={form.pricingType} onChange={e => setField("pricingType", e.target.value)} sx={inputSx}>{PT.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}</TextField></Box>
      </Box>
    );
    if (curType === "package") return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr 1fr 1fr 1fr" }, gap: 2.5 }}>
        <Box><TextField select size="small" fullWidth label="Tier" value={form.category} onChange={e => setField("category", e.target.value)} sx={inputSx}>{PKG_CATS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Box>
        <Box>{nameField}</Box>
        <Box><TextField size="small" fullWidth type="number" label="Hours" value={form.durationHours} onChange={e => setField("durationHours", e.target.value)} inputProps={{ min: 1 }} sx={inputSx} /></Box>
        <Box>{basePriceField}</Box>
        <Box>{priceField}</Box>
      </Box>
    );
    if (curType === "menu") return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr" }, gap: 2.5 }}>
        <Box>{nameField}</Box><Box>{basePriceField}</Box><Box>{priceField}</Box>
        <Box><TextField select size="small" fullWidth label="Category" value={form.category} onChange={e => setField("category", e.target.value)} sx={inputSx}>{menuCatsDynamic.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Box>
      </Box>
    );
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 2fr 1fr 1fr" }, gap: 2.5 }}>
        <Box>{nameField}</Box>
        <Box><TextField select size="small" fullWidth label="Category" value={form.category} onChange={e => setField("category", e.target.value)} sx={inputSx}>{ADDON_CATS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Box>
        <Box>{basePriceField}</Box>
        <Box>{priceField}</Box>
      </Box>
    );
  })();

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 } }}>
      <Box sx={{ pb: { xs: 10, sm: 8 } }}>

        {/* ── Header ── */}
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>In-House Services</Typography>
            <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>Manage internal offerings (menus, bar, add-ons, AV)</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip label={`${items.length} items`} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: T.peach, color: T.navy }} />
            <Button size="small" onClick={() => window.location.href = "/banquet/service-catalogue"}
              sx={{ textTransform: "none", fontWeight: 500, fontSize: 12, borderColor: T.line, color: T.slate, borderRadius: "10px", border: `1px solid ${T.line}`, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
              View in Catalogue
            </Button>
            <Button size="small" onClick={handleSeed} disabled={isSeeding}
              startIcon={isSeeding ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontWeight: 500, fontSize: 12, borderColor: T.line, color: T.slate, borderRadius: "10px", border: `1px solid ${T.line}`, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
              Seed Defaults
            </Button>
          </Box>
        </Box>

        {/* ── Tabs ── */}
        {mcLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
        ) : mainCategories.length === 0 ? (
          <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "14px", border: `1px dashed ${T.line}` }}>
            <ExtensionOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, mb: 1 }} />
            <Typography sx={{ fontSize: 14, color: T.slateLt, mb: 2 }}>No main categories yet.</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setMcEdit(null); setMcModalOpen(true); }}
              sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: T.navy, color: T.navy, borderRadius: "10px" }}>
              Add First Category
            </Button>
          </Paper>
        ) : (
        <Box sx={{ bgcolor: T.white, borderRadius: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)", overflow: "hidden" }}>
          <Box sx={{ borderBottom: `1px solid ${T.line}`, px: { xs: 1, sm: 2 }, display: "flex", alignItems: "center" }}>
            <Tabs value={activeTab} onChange={(_, v) => { if (v < mainCategories.length) setActiveTab(v); }} variant="scrollable" scrollButtons="auto"
              sx={{ flex: 1, "& .MuiTab-root": { textTransform: "none", fontSize: 13, fontWeight: 500, minHeight: 48, color: T.slateLt, gap: 0.8, "&.Mui-selected": { color: T.navy, fontWeight: 600 } }, "& .MuiTabs-indicator": { bgcolor: T.gold, height: 3, borderRadius: "3px 3px 0 0" } }}>
              {mainCategories.map((mc) => (
                <Tab key={mc.id} icon={getTabIcon(mc.icon)} iconPosition="start" label={mc.name} />
              ))}
            </Tabs>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1 }}>
              <Tooltip title="Add Category">
                <IconButton size="small" onClick={() => { setMcEdit(null); setMcModalOpen(true); }}
                  sx={{ color: T.gold, border: `1px dashed ${T.gold}`, width: 30, height: 30, "&:hover": { bgcolor: T.peach } }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {curTab && (
                <>
                  <Tooltip title="Edit Category">
                    <IconButton size="small" onClick={() => { setMcEdit(curTab); setMcModalOpen(true); }}
                      sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                      <EditOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Category">
                    <IconButton size="small" onClick={() => setMcDeleteTarget(curTab)}
                      sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", minHeight: 500 }}>
            {/* ── LEFT: Content ── */}
            <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 2.5 } }}>

              {curType === "led" ? (
                <LEDScreensTab />
              ) : (
              <>
              {/* Category chips + Add new */}
              {catOpts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {/* Filter chips row */}
                  <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip label="All" size="small" onClick={() => setCatFilter("all")}
                      sx={{ height: 26, fontSize: 11, fontWeight: catFilter === "all" ? 600 : 400, bgcolor: catFilter === "all" ? T.navy : T.bg, color: catFilter === "all" ? T.white : T.slate, "&:hover": { bgcolor: catFilter === "all" ? T.navy : T.peach } }} />
                    {catOpts.map(c => (
                      <Chip key={c} label={c} size="small" onClick={() => setCatFilter(c)}
                        onDelete={customCats.includes(c) ? () => { setCustomCats(prev => prev.filter(x => x !== c)); if (catFilter === c) setCatFilter("all"); } : undefined}
                        deleteIcon={customCats.includes(c) ? <CloseIcon sx={{ fontSize: "12px !important" }} /> : undefined}
                        sx={{ height: 26, fontSize: 11, fontWeight: catFilter === c ? 600 : 400, bgcolor: catFilter === c ? T.peach : T.bg, color: catFilter === c ? getColor(c) : T.slate, border: catFilter === c ? `1px solid ${getColor(c)}30` : "1px solid transparent", "&:hover": { bgcolor: T.peach } }} />
                    ))}
                  </Box>

                  {/* Add new category — always visible below chips */}
                  <Box sx={{ mt: 1.5 }}>
                    {showAddCat ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField size="small" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                          placeholder="e.g. Korean, Japanese, Italian..." autoFocus
                          onKeyDown={e => {
                            if (e.key === "Enter" && newCatName.trim()) {
                              const name = newCatName.trim();
                              if (!catOpts.some(c => c.toLowerCase() === name.toLowerCase())) {
                                setCustomCats(prev => [...prev, name]);
                                setCatFilter(name);
                              }
                              setNewCatName(""); setShowAddCat(false);
                            }
                            if (e.key === "Escape") { setNewCatName(""); setShowAddCat(false); }
                          }}
                          sx={{ width: 220, "& .MuiInputBase-root": { height: 32, fontSize: 12, borderRadius: "8px", bgcolor: T.white, border: `1.5px solid ${T.gold}`, "& fieldset": { border: "none" } } }} />
                        <Button size="small" variant="contained" disabled={!newCatName.trim()}
                          onClick={() => {
                            const name = newCatName.trim();
                            if (name && !catOpts.some(c => c.toLowerCase() === name.toLowerCase())) {
                              setCustomCats(prev => [...prev, name]);
                              setCatFilter(name);
                            }
                            setNewCatName(""); setShowAddCat(false);
                          }}
                          sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", boxShadow: "none", height: 32, px: 2, "&:hover": { bgcolor: "#1b3148" } }}>
                          Add
                        </Button>
                        <Button size="small" onClick={() => { setNewCatName(""); setShowAddCat(false); }}
                          sx={{ textTransform: "none", fontSize: 11, color: T.slateLt, minWidth: 0, height: 32 }}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setShowAddCat(true)}
                        sx={{ textTransform: "none", fontSize: 11, fontWeight: 500, color: T.gold, borderRadius: "8px", border: `1px dashed ${T.gold}`, px: 1.5, height: 28, "&:hover": { bgcolor: T.peach, borderColor: T.gold } }}>
                        Add New Category
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 2, bgcolor: T.line, "& .MuiLinearProgress-bar": { bgcolor: T.gold } }} />}

              <Typography sx={{ fontSize: 12, color: T.slateLt, mb: 1.5 }}>{filtered.length} item{filtered.length !== 1 ? "s" : ""}</Typography>

              {/* ── Item list (card-style rows) ── */}
              {filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <RestaurantMenuOutlinedIcon sx={{ fontSize: 48, color: T.line, mb: 1 }} />
                  <Typography sx={{ fontSize: 14, color: T.slateLt, mb: 0.5 }}>No items found</Typography>
                  <Typography sx={{ fontSize: 12, color: T.slateLt }}>Add one below or click Seed Defaults</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {filtered.map(item => {
                    const cat = item.category ?? item.tier ?? "";
                    const isActive = previewItem?.id === item.id;
                    return (
                      <Paper key={item.id} elevation={0}
                        onClick={() => { handleEdit(item); setPreviewItem(item); }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          p: 1.5, borderRadius: "12px", cursor: "pointer",
                          border: `1.5px solid ${isActive ? T.gold : "transparent"}`,
                          bgcolor: isActive ? T.peach : T.white,
                          boxShadow: isActive ? `0 0 0 1px ${T.gold}20` : "0 1px 2px rgba(0,0,0,0.02)",
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: isActive ? T.peach : "#fef6ee", borderColor: isActive ? T.gold : T.line },
                        }}>
                        {/* Image */}
                        <Box component="img" src={item.image || getThumb(cat, item.name)} onError={(e: any) => { e.target.src = PH; }}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); const g = item.image ? [item.image, ...getGallery(cat, item.name).slice(0, 2)] : getGallery(cat, item.name); openGallery(g); }}
                          sx={{ width: 48, height: 48, borderRadius: "10px", objectFit: "cover", flexShrink: 0, cursor: "zoom-in", transition: "transform 0.15s", "&:hover": { transform: "scale(1.08)" } }} />
                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>{item.name}</Typography>
                          <Box sx={{ display: "flex", gap: 0.6, mt: 0.4, flexWrap: "wrap" }}>
                            {cat && <Chip label={cat} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, color: getColor(cat), bgcolor: `${getColor(cat)}10`, border: `1px solid ${getColor(cat)}20` }} />}
                            {item.pricingType && <Chip label={item.pricingType === "pp" ? "PP" : item.pricingType === "flat" ? "Flat" : "Incl"} size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.bg, color: T.slate }} />}
                            {item.durationHours && <Chip label={`${item.durationHours}hr`} size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.bg, color: T.slate }} />}
                          </Box>
                        </Box>
                        {/* Price with base price indicator */}
                        <PriceInline price={item.price} basePrice={item.base_price} />
                        {/* Actions */}
                        <Box sx={{ display: "flex", flexShrink: 0, gap: 0.2 }} onClick={e => e.stopPropagation()}>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditModalItem(item); setPreviewItem(item); if (item.images && item.images.length > 0) setServiceImages(item.images); else fetchServiceImages(item.id); }} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}><EditOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(item)} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}><DeleteOutlineIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}

              {/* ── Add / Edit form ── */}
              <Box ref={formRef} sx={{ mt: 3, bgcolor: isEditing ? T.peach : T.bg, borderRadius: "12px", p: 2.5, border: isEditing ? `1.5px solid ${T.gold}` : "1.5px solid transparent", transition: "all 0.2s" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isEditing ? <EditOutlinedIcon sx={{ fontSize: 16, color: T.gold }} /> : <AddIcon sx={{ fontSize: 16, color: T.gold }} />}
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{isEditing ? "Edit Item" : "Add Item"}</Typography>
                    {isEditing && <Chip label={`#${form.id}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: T.peach, color: T.navy }} />}
                  </Box>
                </Box>
                {formFields}

                {/* ── Price warning ── */}
                {priceDiffers && (
                  <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.8, bgcolor: "#fef6ee", borderRadius: "8px", border: "1px solid #fed7aa" }}>
                    <WarningAmberIcon sx={{ fontSize: 15, color: "#e67e22" }} />
                    <Typography sx={{ fontSize: 11, color: "#c2410c", fontWeight: 500 }}>
                      Selling price (${form.price}) differs from base price (${form.base_price})
                    </Typography>
                  </Box>
                )}

                {/* ── Image Upload ── */}
                <Box sx={{ mt: 2.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.2 }}>
                    Item Image
                  </Typography>
                  <ImageUploader
                    imageUrl={form.image}
                    onImageChange={(url) => setField("image", url)}
                  />
                </Box>

                {/* ── Media Manager ── */}
                {(curType === "menu" || curType === "food" || curType === "package") && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                      Media ({media.length} item{media.length !== 1 ? "s" : ""})
                    </Typography>

                    {/* Thumbnails grid */}
                    {media.length > 0 && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {media.map((m, i) => (
                          <Box key={i} sx={{ position: "relative", width: 72, height: 72, borderRadius: "10px", overflow: "hidden", border: `1px solid ${T.line}` }}>
                            {m.type === "image" ? (
                              <Box component="img" src={m.url} onError={(e: any) => { e.target.src = PH; }}
                                onClick={() => openGallery(media.filter(x => x.type === "image").map(x => x.url), media.filter(x => x.type === "image").findIndex(x => x.url === m.url))}
                                sx={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
                            ) : (
                              <Box sx={{ width: "100%", height: "100%", bgcolor: "#111", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <PlayCircleOutlineIcon sx={{ color: "#fff", fontSize: 24 }} />
                              </Box>
                            )}
                            {/* Reorder arrows */}
                            <Box sx={{ position: "absolute", bottom: 2, left: 2, display: "flex", gap: "2px" }}>
                              {i > 0 && (
                                <Box onClick={() => { const a = [...media]; [a[i], a[i-1]] = [a[i-1], a[i]]; setMedia(a); }}
                                  sx={{ width: 16, height: 16, borderRadius: "4px", bgcolor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                                  <ArrowBackIosNewIcon sx={{ fontSize: 8, color: "#fff" }} />
                                </Box>
                              )}
                              {i < media.length - 1 && (
                                <Box onClick={() => { const a = [...media]; [a[i], a[i+1]] = [a[i+1], a[i]]; setMedia(a); }}
                                  sx={{ width: 16, height: 16, borderRadius: "4px", bgcolor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                                  <ArrowForwardIosIcon sx={{ fontSize: 8, color: "#fff" }} />
                                </Box>
                              )}
                            </Box>
                            {/* Delete */}
                            <Box onClick={() => setMedia(media.filter((_, j) => j !== i))}
                              sx={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", bgcolor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "rgba(200,50,50,0.8)" } }}>
                              <CloseIcon sx={{ fontSize: 10, color: "#fff" }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Add media URL */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField size="small" fullWidth value={mediaUrl} onChange={e => setMediaUrl(e.target.value)}
                        placeholder="Paste image URL or YouTube link..."
                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
                        onKeyDown={e => {
                          if (e.key === "Enter" && mediaUrl.trim()) {
                            const isVideo = mediaUrl.includes("youtube.com") || mediaUrl.includes("youtu.be");
                            setMedia(prev => [...prev, { type: isVideo ? "video" : "image", url: mediaUrl.trim() }]);
                            setMediaUrl("");
                          }
                        }}
                        sx={inputSx} />
                      <Button size="small" variant="outlined" disabled={!mediaUrl.trim()}
                        onClick={() => {
                          if (!mediaUrl.trim()) return;
                          const isVideo = mediaUrl.includes("youtube.com") || mediaUrl.includes("youtu.be");
                          setMedia(prev => [...prev, { type: isVideo ? "video" : "image", url: mediaUrl.trim() }]);
                          setMediaUrl("");
                        }}
                        startIcon={<AddPhotoAlternateOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: "none", fontSize: 11, borderColor: T.line, color: T.navy, borderRadius: "8px", whiteSpace: "nowrap", "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
                  <Button size="small" onClick={handleClear} sx={{ textTransform: "none", fontSize: 12, color: T.slate, borderRadius: "8px" }}>Clear</Button>
                  <Button size="small" variant="contained" onClick={handleSave} disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: "#1b3148" } }}>
                    {isSaving ? "Saving..." : isEditing ? "Update" : "Save"}
                  </Button>
                </Box>
              </Box>
              </>
              )}
            </Box>

            {/* ── RIGHT: Preview Panel (desktop, not for LED tab) ── */}
            {!isMd && curType !== "led" && (
              <Box sx={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${T.line}`, bgcolor: T.bg, p: 2.5, display: "flex", flexDirection: "column" }}>
                {previewItem ? (() => {
                  const cat = previewItem.category ?? previewItem.tier ?? "";
                  const defaultGallery = getGallery(cat, previewItem.name);
                  const gallery = previewItem.image ? [previewItem.image, ...defaultGallery.slice(0, 2)] : defaultGallery;
                  return (
                    <Box sx={{ animation: "fadeSlide 0.2s ease", "@keyframes fadeSlide": { from: { opacity: 0, transform: "translateY(8px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                      {/* Gallery grid */}
                      <Box sx={{ display: "grid", gridTemplateColumns: gallery.length >= 3 ? "2fr 1fr" : "1fr", gridTemplateRows: gallery.length >= 3 ? "1fr 1fr" : "auto", gap: 0.8, mb: 2, borderRadius: "14px", overflow: "hidden", height: 220 }}>
                        {/* Main large image */}
                        <Box
                          component="img" src={gallery[0]} onError={(e: any) => { e.target.src = PH; }}
                          onClick={() => openGallery(gallery, 0)}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in", gridRow: gallery.length >= 3 ? "1 / 3" : "auto", transition: "filter 0.2s", "&:hover": { filter: "brightness(0.9)" } }}
                        />
                        {/* Second image */}
                        {gallery.length >= 2 && (
                          <Box component="img" src={gallery[1]} onError={(e: any) => { e.target.src = PH; }}
                            onClick={() => openGallery(gallery, 1)}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in", transition: "filter 0.2s", "&:hover": { filter: "brightness(0.9)" } }} />
                        )}
                        {/* Third image with "+N more" overlay */}
                        {gallery.length >= 3 && (
                          <Box sx={{ position: "relative", cursor: "zoom-in" }} onClick={() => openGallery(gallery, 2)}>
                            <Box component="img" src={gallery[2]} onError={(e: any) => { e.target.src = PH; }}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            {gallery.length > 3 && (
                              <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>+{gallery.length - 3}</Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                      {/* Name */}
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.navy, lineHeight: 1.2, mb: 0.5 }}>{previewItem.name}</Typography>
                      {/* Category + chips */}
                      <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mb: 2 }}>
                        {cat && <Chip label={cat} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, color: getColor(cat), bgcolor: `${getColor(cat)}10`, border: `1px solid ${getColor(cat)}20` }} />}
                        {previewItem.pricingType === "pp" && <Chip label="Per Person" size="small" sx={{ height: 22, fontSize: 10, bgcolor: T.greenBg, color: T.greenTx }} />}
                        {previewItem.durationHours && <Chip label={`${previewItem.durationHours} hours`} size="small" sx={{ height: 22, fontSize: 10, bgcolor: "#e3f2fd", color: "#1565c0" }} />}
                      </Box>
                      {/* Price card with base price */}
                      <PriceCard price={previewItem.price} basePrice={previewItem.base_price} pricingType={previewItem.pricingType} />
                      {/* Edit button */}
                      <Button fullWidth variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => { setEditModalItem(previewItem); if (previewItem.images && previewItem.images.length > 0) setServiceImages(previewItem.images); else fetchServiceImages(previewItem.id); }}
                        sx={{ textTransform: "none", fontSize: 12, fontWeight: 500, borderColor: T.line, color: T.navy, borderRadius: "10px", "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
                        Edit This Item
                      </Button>
                    </Box>
                  );
                })() : (
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                    <RestaurantMenuOutlinedIcon sx={{ fontSize: 48, color: T.line, mb: 1.5 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: T.slateLt, mb: 0.5 }}>Select an item</Typography>
                    <Typography sx={{ fontSize: 12, color: T.slateLt }}>Click any item to see a preview</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        )}
      </Box>

      {/* Main Category Add/Edit Modal */}
      <Dialog open={mcModalOpen} onClose={() => { setMcModalOpen(false); setMcEdit(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SettingsOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
              {mcEdit?.id ? "Edit Main Category" : "New Main Category"}
            </Typography>
          </Box>
          <IconButton onClick={() => { setMcModalOpen(false); setMcEdit(null); }} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField label="Category Name *" size="small" autoFocus fullWidth
            defaultValue={mcEdit?.name || ""}
            inputRef={(el: HTMLInputElement | null) => { if (el && mcModalOpen) el.value = mcEdit?.name || ""; }}
            key={mcEdit?.id ?? "new"}
            sx={inputSx}
            id="mc-name" />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Type (internal key)" size="small"
              defaultValue={mcEdit?.type || ""}
              key={`type-${mcEdit?.id ?? "new"}`}
              sx={inputSx}
              id="mc-type"
              helperText="e.g. food, bar, menu" />
            <TextField label="Sort Order" size="small" type="number"
              defaultValue={mcEdit?.sort_order ?? ""}
              key={`sort-${mcEdit?.id ?? "new"}`}
              sx={inputSx}
              id="mc-sort" />
          </Box>
        </DialogContent>
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
          <Button onClick={() => { setMcModalOpen(false); setMcEdit(null); }} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
          <Button variant="contained" disabled={mcSaving}
            onClick={async () => {
              const nameEl = document.getElementById("mc-name") as HTMLInputElement;
              const typeEl = document.getElementById("mc-type") as HTMLInputElement;
              const sortEl = document.getElementById("mc-sort") as HTMLInputElement;
              const name = nameEl?.value?.trim();
              if (!name) return;
              setMcSaving(true);
              const res = await mcApi.post("/inhouse/main-categories", {
                id: mcEdit?.id,
                name,
                type: typeEl?.value?.trim() || null,
                sort_order: sortEl?.value ? Number(sortEl.value) : undefined,
              });
              if (res.code === 200 || res.status) {
                showMessage(res.message ?? "Saved");
                setMcModalOpen(false); setMcEdit(null);
                fetchMainCategories();
              } else showErrorMessage(res.message ?? "Failed");
              setMcSaving(false);
            }}
            startIcon={mcSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
            {mcSaving ? "Saving..." : "Save Category"}
          </Button>
        </Box>
      </Dialog>

      {/* Main Category Delete Dialog */}
      <Dialog open={!!mcDeleteTarget} onClose={() => setMcDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete category?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Delete <strong>{mcDeleteTarget?.name}</strong>? Items in this category will remain but won't be grouped.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMcDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={mcDeleting}
            onClick={async () => {
              if (!mcDeleteTarget) return;
              setMcDeleting(true);
              const res = await mcApi.del(`/inhouse/main-categories/${mcDeleteTarget.id}`);
              if (res.code === 200 || res.status) {
                showMessage("Category deleted");
                if (activeTab >= mainCategories.length - 1) setActiveTab(Math.max(0, activeTab - 1));
                fetchMainCategories();
              } else showErrorMessage(res.message ?? "Failed");
              setMcDeleting(false);
              setMcDeleteTarget(null);
            }}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>
            {mcDeleting ? "..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}><WarningAmberIcon color="error" /> Delete?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Delete <strong>{deleteTarget?.name}</strong> permanently?</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", borderRadius: "8px" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={isDeleting} sx={{ textTransform: "none", borderRadius: "8px", boxShadow: "none" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Service Modal */}
      <EditServiceModal
        open={!!editModalItem}
        onClose={() => setEditModalItem(null)}
        item={editModalItem as EditServiceItem | null}
        onSave={handleModalSave}
        isSaving={isSaving}
        categoryOptions={catOpts}
        serviceType={curType}
        serviceImages={serviceImages}
        onUploadImage={handleSvcImgUpload}
        onDeleteImage={handleDeleteSvcImg}
        onSetCover={handleSetCover}
        onReorderImage={handleSvcImgReorder}
        onUpdateImageTitle={handleUpdateSvcImgTitle}
        onUpdateImageTag={handleUpdateSvcImgTag}
        isUploadingImage={svcImgUploading}
      />

      {/* Gallery lightbox with navigation */}
      {galleryIdx >= 0 && galleryImages.length > 0 && (
        <Box onClick={closeGallery} sx={{ position: "fixed", inset: 0, zIndex: 2000, bgcolor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", animation: "fadeIn 0.15s", "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          {/* Image */}
          <Box component="img" src={galleryImages[galleryIdx]} onError={(e: any) => { e.target.src = PH; }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ maxWidth: "85vw", maxHeight: "80vh", borderRadius: "14px", objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.5)", cursor: "default", animation: "zoomIn 0.2s", "@keyframes zoomIn": { from: { transform: "scale(0.94)", opacity: 0 }, to: { transform: "scale(1)", opacity: 1 } } }} />

          {/* Counter */}
          <Box sx={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", bgcolor: "rgba(0,0,0,0.5)", borderRadius: "20px", px: 2, py: 0.5 }}>
            <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 500 }}>{galleryIdx + 1} / {galleryImages.length}</Typography>
          </Box>

          {/* Prev */}
          {galleryIdx > 0 && (
            <IconButton onClick={(e) => { e.stopPropagation(); setGalleryIdx(i => i - 1); }}
              sx={{ position: "absolute", left: { xs: 8, md: 24 }, color: "#fff", bgcolor: "rgba(255,255,255,0.1)", width: 44, height: 44, "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Next */}
          {galleryIdx < galleryImages.length - 1 && (
            <IconButton onClick={(e) => { e.stopPropagation(); setGalleryIdx(i => i + 1); }}
              sx={{ position: "absolute", right: { xs: 8, md: 24 }, color: "#fff", bgcolor: "rgba(255,255,255,0.1)", width: 44, height: 44, "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
              <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Close */}
          <IconButton onClick={closeGallery}
            sx={{ position: "absolute", top: 16, right: 16, color: "#fff", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <CloseIcon />
          </IconButton>

          {/* Thumbnail strip */}
          {galleryImages.length > 1 && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ position: "absolute", bottom: 60, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 1 }}>
              {galleryImages.map((img, i) => (
                <Box key={i} component="img" src={img.replace("w=800&h=600", "w=60&h=60")} onError={(e: any) => { e.target.src = PH; }}
                  onClick={() => setGalleryIdx(i)}
                  sx={{ width: 48, height: 48, borderRadius: "8px", objectFit: "cover", cursor: "pointer", border: i === galleryIdx ? "2px solid #c9a96e" : "2px solid transparent", opacity: i === galleryIdx ? 1 : 0.6, transition: "all 0.15s", "&:hover": { opacity: 1 } }} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default BanquetServices;
